<template>
  <section>
    <section class="d-flex mb-2">
      <div
        v-if="value && value.length"
        class="position-relative"
      >
        <input
          ref="fileInput"
          type="file"
          class="file-input position-absolute"
          multiple
          @change="addFile"
        />
        <AppSpinner
          v-if="isUploading"
          class="mr-2"
          variant="primary"
        />
        <AppButton
          v-else
          class="mr-2"
          :raised="true"
          :disabled="isUploading"
          @click="openFileBrowser"
        >
          <AppIcon
            name="attachment"
            class="mr-2"
          /> Upload
        </AppButton>
      </div>
      <AppButton @click="modals.info=true">
        <AppIcon
          name="help_outline"
          class="mr-2"
        /> Learn More
      </AppButton>
    </section>
    <div
      v-if="!value || !value.length"
      class="my-2 rounded-5 border p-5 upload-box d-flex position-relative align-items-center justify-content-center"
      :class="{'bg-light-gray':isDragging,'bg-light':!isDragging}"
      @dragleave="isDragging=false"
      @drop="isDragging=false"
      @dragenter="isDragging=true"
    >
      <div class="text-center text-muted">
        <div>
          <AppSpinner
            v-if="isUploading"
            variant="primary"
          />
          <AppIcon
            v-else
            name="backup"
          />
        </div>
        <div>Click or drag file to upload</div>
        <div>
          <AppBadge
            v-for="fileType in suportedFiles"
            :key="fileType"
            class="mr-2"
            variant="secondary"
          >.{{ fileType }}</AppBadge>
        </div>
      </div>
      <input
        ref="fileInput"
        type="file"
        class="file-input position-absolute"
        multiple
        @change="addFile"
      />
    </div>
    <AdvanceRequisitesListEditable
      :value="value"
      :config="config"
      @input="updateRequisites"

    />
    <AppModal
      v-if="modals.info"
      width="650px"
      :useDimness="true"
      :noBodyOverflow="true"
      @close="modals.info = false"
    >
      <template #modal-header>
        <AppHeading>Advanced Requisite Inputs</AppHeading>
      </template>
      <template #modal-body>
        <AppTable
          :rows="fieldDescription"
          resourceName="Field Description"
          striped
        >
          <template #header>
            <th class="th">Field</th>
            <th class="th">Description</th>
          </template>
          <template #default="{ row: field }">
            <td class="font-weight-bold">{{ field.label }}</td>
            <td>{{ field.description }}</td>
          </template>
        </AppTable>
      </template>
    </AppModal>
    <AppModal
      v-if="modals.error"
      width="500px"
      :useDimness="true"
      :noBodyOverflow="true"
      @close="closeErrorModal"
    >
      <template #modal-header>
        <AppHeading>Upload Failed</AppHeading>
      </template>
      <template #modal-body>
        <AppAlert
          variant="danger"
          class="rounded"
        >
          {{ errorMessage }}
        </AppAlert>
      </template>
    </AppModal>
    <AppModal
      v-if="modals.warning"
      width="500px"
      :useDimness="true"
      :noBodyOverflow="true"
      @close="closeWarningModal"
    >
      <template #modal-header>
        <AppHeading>Upload Succeeded With Warning</AppHeading>
      </template>
      <template #modal-body>
        <AppAlert
          variant="warning"
          class="rounded"
        >
          {{ warningMessage }}
        </AppAlert>
      </template>
    </AppModal>
  </section>
</template>

<script>
import AdvanceRequisitesListEditable from './AdvanceRequisitesListEditable'
import { postScribeFile, uploadToS3 } from '@/api'
import { mapGetters, mapMutations } from 'vuex'
import FormMixin from '@/components/Forms/FormMixin'

export default {
  mixins: [ FormMixin ],
  components: {
    AdvanceRequisitesListEditable,
  },
  props: {
    value: {
      type: Array,
      default: () => ([]),
    },
    parentValue: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    return {
      file: null,
      isUploading: false,
      isDragging: false,
      errorMessage: null,
      warningMessage: null,
      modals: {
        info: false,
        error: false,
        warning: false,
      },
    }
  },
  computed: {
    ...mapGetters([ 'school' ]),
    suportedFiles () {
      return [ 'scribe', 'txt', 'text' ]
    },
    fieldDescription () {
      return [
        {
          label: 'Condition',
          description: 'Condition for the current rule',
        },
        {
          label: 'Min',
          description: 'Minimum value for the current rule',
        },
        {
          label: 'Max',
          description: 'Maximum value for the current rule',
        },
        {
          label: 'Type',
          description: 'Type for the Min/Max value',
        },
        {
          label: 'Blocks',
          description: 'Type of credentials that can be used to satisfy the rule',
        },
        {
          label: 'Text',
          description: 'Any free form text for the rule',
        },
        {
          label: 'Sub Rules',
          description: 'To set any child rules',
        },
        {
          label: 'Priority',
          description: 'Priority of the current rule, 0 is neutral priority, positive numbers are high priority, negative numbers are low priority',
        },
        {
          label: 'Tag',
          description: 'May be set on any rule to give it a unique identifier.',
        },
        {
          label: 'Courses',
          description: 'Courses under the current rule',
        },
      ]
    },
  },
  watch: {
    async file () {
      if (!this.file || !this.file.path) return
      this.isUploading = true
      const fileKey = this.file.path

      const {
        parsed: rules,
        relatedData,
        error,
        warning } = await postScribeFile({ school: this.school, fileKey, parentId: this.parentValue._id })
      if (error) {
        const data = error.response.data
        this.openErrorModal(`Error: ${data.error}, found ${data.token} on line ${data.line}`)
      } else {
        if (relatedData && relatedData.courses && Object.keys(relatedData.courses).length) {
          this.setCmCourses(relatedData.courses)
        }
        if (relatedData && relatedData.departments && Object.keys(relatedData.departments).length) {
          this.setDepartments(relatedData.departments)
        }
        // TODO: BE should send formatted requisite with nested rules instead of sending the rules directly
        const name = this.file.name ? this.file.name.split('.')[0] : ''
        this.updateRequisites([ ...this.value, { rules, name: name || 'Untitled Requirement', freeFormText: '' } ])
      }
      this.isUploading = false
      if (warning) this.openWarningModal(`Warning: ${warning}`)
    },
  },
  methods: {
    ...mapMutations('curriculumCourses', [ 'setCmCourses' ]),
    ...mapMutations('departments', [ 'setDepartments' ]),
    openFileBrowser () {
      this.$refs.fileInput.click()
    },
    async addFile () {
      this.file = null
      this.isUploading = true
      const file = this.$refs.fileInput.files[0]
      if (!file) {
        this.isUploading = false
        return
      }
      try {
        const fileExt = file.name.split('.').pop()
        if (!this.suportedFiles.includes(fileExt.toLowerCase())) {
          this.file = null
          this.openErrorModal(`.${fileExt} files are not allowed`)
          this.isUploading = false
          return
        }
        const fileKey = `files/scribe/${Date.now()}/${this.generateToken(12)}.${fileExt}`
        await uploadToS3({
          school: this.school,
          file,
          fileKey,
        })
        this.file = { name: file.name, path: fileKey }
      } catch (error) {
        this.file = null
        this.openErrorModal(`Something went wrong for file ${file.name}, please try another file`)
      }
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
      this.isUploading = false
    },
    updateRequisites (value) {
      this.$emit('input', value)
    },
    openErrorModal (message) {
      this.errorMessage = message
      this.modals.error = true
    },
    openWarningModal (message) {
      this.warningMessage = message
      this.modals.warning = true
    },
    closeErrorModal () {
      this.errorMessage = null
      this.modals.error = false
    },
    closeWarningModal (message) {
      this.warningMessage = null
      this.modals.warning = false
    },
  },
}
</script>

<style scoped>
.file-input {
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}
</style>
